import { useEffect, useMemo } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { getClientPublications, getClientPublicationsQueryKey } from '@/api/kubik/infoportal'
import { ResponsiveBlocksPerGrid } from '@/components/resolvers/Articles/ResponsiveBlocksPerGrid'
import { useResponsiveValue } from '@/utils/hooks/responsive'
import type { IUseJournalPublicationsProps } from '@/utils/infoportal/hooks/types'

export const useInfiniteJournalPublications = (props: IUseJournalPublicationsProps) => {
  const { type, rubricId, tagId, filteredDiff = 0, extraKey = 'simpleArticles' } = props

  const itemsPerPage = useResponsiveValue(ResponsiveBlocksPerGrid)
  const itemsToRender = itemsPerPage + filteredDiff

  const { data, isInitialLoading, isFetching, isSuccess, isError, hasNextPage, fetchNextPage, refetch } = useInfiniteQuery({
    queryFn: ({ pageParam }) =>
      getClientPublications({
        type,
        limit: itemsToRender,
        offset: pageParam,
        rubricId,
        tagId,
      }),
    queryKey: [...getClientPublicationsQueryKey({ rubricId, tagId, type }), 'infiniteArticles', extraKey],
    getNextPageParam: lastPage => {
      if (
        typeof lastPage.data?.pagination.offset === 'number' &&
        lastPage.data.pagination.offset + itemsToRender >= lastPage.data.pagination.total
      )
        return undefined
      if (!lastPage.data) return undefined

      return lastPage.data.pagination.offset + itemsToRender
    },
    staleTime: Infinity,
  })

  useEffect(() => {
    if (filteredDiff > 0) refetch()
  }, [filteredDiff, refetch])

  const publications = useMemo(() => data?.pages.map(page => page.data?.publications || []).flat(), [data])

  const getNextPage = () => {
    if (hasNextPage) fetchNextPage()
  }

  return {
    paginatedPublications: data?.pages,
    publications,
    isPublicationsInitialLoading: isInitialLoading,
    isPublicationsLoading: isFetching,
    isPublicationsLoaded: isSuccess,
    isPublicationsError: isError,
    hasNextPage,
    getNextPage,
  }
}
